/**
 * Interactive inputs (KAN-1702 contract, KAN-1703 viewer).
 *
 * A module declares in its manifest which input port the customer may change
 * straight in a visualization block, and with which control
 * (``interactive_inputs: [{key, control, view_kind, default_from}]``). The
 * platform owns the palette of controls — this file is its FE half — and never
 * names a port: the key comes from the declaration, the value goes back to the
 * generic reanalysis as ``graph_inputs[key]`` (KAN-1701).
 *
 * The BE hands the viewer the declaration already resolved against this part:
 * ``value`` is the stored customer pick (KAN-1700), else the module's
 * recommendation (``default_from``), else the port default; ``default`` is the
 * recommendation alone, so "back to recommended" has something to show before
 * the server answers.
 */
import type { InteractiveInputState } from "@/api/types";
import type { MeshRotation90 } from "@/components/MeshPreview";

export type { InteractiveInputState };

/** What a viewer needs to draw controls and report a change. */
export interface InteractiveBinding {
  inputs: InteractiveInputState[];
  /** ``null`` = back to the recommendation (the server resolves it). */
  onChange: (key: string, value: unknown) => void;
  /** The last change could not be recomputed; the controls show the stored state. */
  failed?: boolean;
}

/** Quarter turns about X, Y and Z (0–3 each). */
export const QUARTER_TURNS_3D = "quarter_turns_3d";

/** Controls this FE can render, with the blocks each may sit on. Mirrors BE
 *  ``manifest.INTERACTIVE_CONTROLS`` — a control without a renderer here can't
 *  be declared there. */
export const INTERACTIVE_CONTROL_KINDS: Record<string, readonly string[]> = {
  [QUARTER_TURNS_3D]: ["model3d"],
};

/** Inputs whose control this FE renders on the given block. */
export function inputsForView(
  inputs: readonly InteractiveInputState[] | null | undefined,
  viewKind: string,
): InteractiveInputState[] {
  return (inputs ?? []).filter(
    (i) =>
      i.view_kind === viewKind &&
      (INTERACTIVE_CONTROL_KINDS[i.control] ?? []).includes(viewKind),
  );
}

function quarter(value: unknown): 0 | 1 | 2 | 3 {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0;
  return ((((Math.round(value) % 4) + 4) % 4) as 0 | 1 | 2 | 3);
}

/** ``[x, y, z]`` quarter turns → mesh rotation; ``null`` for anything else. */
export function quarterTurnsOf(value: unknown): MeshRotation90 | null {
  if (!Array.isArray(value) || value.length !== 3) return null;
  return { x: quarter(value[0]), y: quarter(value[1]), z: quarter(value[2]) };
}

/** The part as uploaded — what a viewer draws when the part has no such input. */
export const NO_ROTATION: MeshRotation90 = { x: 0, y: 0, z: 0 };

/** Value of a ``quarter_turns_3d`` input: current, else recommended, else none. */
export function rotationOfInputs(
  inputs: readonly InteractiveInputState[] | null | undefined,
): MeshRotation90 | null {
  const input = (inputs ?? []).find((i) => i.control === QUARTER_TURNS_3D);
  if (!input) return null;
  return quarterTurnsOf(input.value) ?? quarterTurnsOf(input.default);
}

/** Interactive inputs of the path's primary operation (``order_index`` 0),
 *  where the BE stores the values (KAN-1700). */
export function primaryInteractiveInputs(
  operations:
    | readonly { order_index: number; interactive_inputs?: InteractiveInputState[] | null }[]
    | null
    | undefined,
): InteractiveInputState[] {
  return operations?.find((op) => op.order_index === 0)?.interactive_inputs ?? [];
}
